import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  styled,
  TextField,
} from '@mui/material';
import PageHeader from '../../controls/PageHeader';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import TextSearch from '../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useState } from 'react';
import * as accountingSlice from '../../../data/accounting.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  GridColDef,
  GridRowClassNameParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { DataGridPro } from '../../tables/DataGridPro';
import { TransportationService } from '../../../data/models/transportation-service.model';
import { hasWordStartingWith } from '../../../utils/string.utils';
import { CommonEntityState } from '../../../data/models/entity-state';
import CommonEntityStateChip from '../shared/CommonEntityStateChip';
import { MoreVert } from '@mui/icons-material';
import ServiceLevelModelDialog, {
  ServiceLevelModelDialogAction,
} from './ServiceLevelModelDialog';
import ActivateDialog from '../ActivateDialog';
import ConfirmationDialog from '../../ConfirmationDialog';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ServiceLevelModels';

const classes = {
  container: `${PREFIX}-container`,
  subHeader: `${PREFIX}-header`,
  contentContainer: `${PREFIX}-contentContainer`,
  search: `${PREFIX}-search`,
  statusSelect: `${PREFIX}-statusSelect`,
  addButton: `${PREFIX}-addButton`,
  moreButtonContainer: `${PREFIX}-moreButtonContainer`,
  moreButton: `${PREFIX}-moreButton`,
  clickableRow: `${PREFIX}-clickableRow`,
  nonClickableRow: `${PREFIX}-nonClickableRow`,
  hoveredRow: `${PREFIX}-hoveredRow`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(1),
  },
  [`& .${classes.contentContainer}`]: {
    padding: theme.spacing(2),
  },
  [`& .${classes.subHeader}`]: {
    justifyContent: 'space-between',
  },
  [`& .${classes.search}`]: {
    width: 320,
    marginRight: theme.spacing(3),
  },
  [`& .${classes.statusSelect}`]: {
    width: 200,
  },
  [`& .${classes.addButton}`]: {
    width: 150,
  },
  [`& .${classes.moreButtonContainer}`]: {
    display: 'flex',
    justifyContent: 'end',
    width: '100%',
  },
  [`& .${classes.moreButton}`]: {
    marginRight: theme.spacing(1),
  },
}));

const StyledDataGridPro = styled(DataGridPro)(({ theme }) => ({
  cursor: 'pointer',
  [`& .${classes.hoveredRow}`]: {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    minWidth: '164px',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  ...theme.typography.body2,
}));

export default function ServiceLevelModels() {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const isSuperReaderUser = useIsSuperReaderUser();

  const allStatuses = t('serviceLevelModels.all');

  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(allStatuses);
  const [hoveredRow, setHoveredRow] = useState<string | undefined>(undefined);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuOpenedRow, setMenuOpenedRow] = useState<string | null>(null);
  const [selectedServiceLevelModel, setSelectedServiceLevelModel] =
    useState<TransportationService | null>(null);
  const [serviceLevelModelDialogOpen, setServiceLevelModelDialogOpen] =
    useState(false);
  const [serviceLevelModelDialogAction, setServiceLevelModelDialogAction] =
    useState<ServiceLevelModelDialogAction | undefined>(undefined);
  const [isDeactivate, setIsDeactivate] = useState(false);
  const [activateDialogTitle, setActivateDialogTitle] = useState('');
  const [activateDialogMessage, setActivateDialogMessage] = useState('');
  const [activateDialogPostiveFn, setActivateDialogPostiveFn] = useState<
    () => void
  >(() => null);
  const [activateDialogNegativeFn, setActivateDialogNegativeFn] = useState<
    () => void
  >(() => null);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const allServiceLevelModels = useAppSelector(
    accountingSlice.selectTransportationServices,
  );
  const filteredServiceLeveModels = allServiceLevelModels.filter(
    (serviceLevelModel) =>
      hasWordStartingWith(serviceLevelModel.name?.toLowerCase(), searchText),
  );

  const findServiceLevelModel = (transportationServiceUUID: string) => {
    return allServiceLevelModels.find(
      (serviceLevelModel) =>
        serviceLevelModel.transportationServiceUUID ===
        transportationServiceUUID,
    );
  };

  useEffect(() => {
    const states =
      selectedStatus && selectedStatus !== allStatuses
        ? [selectedStatus]
        : undefined;
    dispatch(accountingSlice.fetchTransportationServicesAsync(states));
  }, [selectedStatus]);

  const isEditable = (state?: CommonEntityState) =>
    state === CommonEntityState.Draft || state === CommonEntityState.Inactive;

  const selectableStatuses = [
    CommonEntityState.Active,
    CommonEntityState.Inactive,
    CommonEntityState.Draft,
  ];

  const statuses = selectableStatuses.map((status) => ({
    key: status as string,
    value: t(`common.${status}`),
  }));
  statuses.unshift({ key: allStatuses, value: allStatuses });

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setHoveredRow(undefined);
    setMenuOpenedRow(null);
  };

  const handleSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
  };

  const handleAddServiceLevelModelClick = () => {
    setSelectedServiceLevelModel(null);
    setServiceLevelModelDialogAction(ServiceLevelModelDialogAction.Add);
    setServiceLevelModelDialogOpen(true);
  };

  const handleRowClick = (params: GridRowParams) => {
    const serviceLevelModelUUID = params.row.transportationServiceUUID;
    const selectedServiceLevelModel = findServiceLevelModel(
      serviceLevelModelUUID,
    );
    if (selectedServiceLevelModel) {
      setSelectedServiceLevelModel(selectedServiceLevelModel);
      if (isEditable(params.row.state)) {
        setServiceLevelModelDialogAction(ServiceLevelModelDialogAction.Edit);
      } else {
        setServiceLevelModelDialogAction(ServiceLevelModelDialogAction.View);
      }
      setServiceLevelModelDialogOpen(true);
    }
  };

  const handleMenuClose = () => {
    closeMenu();
  };

  const handleEditClick = () => {
    closeMenu();
    if (selectedServiceLevelModel) {
      setServiceLevelModelDialogAction(ServiceLevelModelDialogAction.Edit);
      setServiceLevelModelDialogOpen(true);
    }
  };

  const handleSetActiveClick = () => {
    closeMenu();
    if (selectedServiceLevelModel?.transportationServiceUUID) {
      activatePricingModel(selectedServiceLevelModel.transportationServiceUUID);
    }
  };

  const handleSetInactiveClick = () => {
    closeMenu();
    if (selectedServiceLevelModel?.transportationServiceUUID) {
      deactivatePricingModel(
        selectedServiceLevelModel.transportationServiceUUID,
      );
    }
  };

  const handleDeleteClick = () => {
    closeMenu();
    setConfirmDialogOpen(true);
  };

  const handleDuplicateClick = () => {
    closeMenu();
    if (selectedServiceLevelModel) {
      setServiceLevelModelDialogAction(ServiceLevelModelDialogAction.Duplicate);
      setServiceLevelModelDialogOpen(true);
    }
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    transportationServiceUUID: string,
  ) => {
    event.stopPropagation();

    setMenuOpenedRow(transportationServiceUUID);
    setMenuAnchorEl(event.currentTarget);
    const selectedServiceLevelModel = findServiceLevelModel(
      transportationServiceUUID,
    );
    setSelectedServiceLevelModel(selectedServiceLevelModel ?? null);
  };

  const handleServiceLevelModelDialogClose = () => {
    setServiceLevelModelDialogOpen(false);
    setSelectedServiceLevelModel(null);
  };

  const updatePricingModelEffectiveDates = (
    transportationServiceUUID: string,
    start?: string,
    end?: string,
  ) => {
    dispatch(
      accountingSlice.updateTransportationServiceEffectiveDatesAsync(
        transportationServiceUUID,
        start,
        end,
      ),
    );
  };

  const activatePricingModel = (id: string) => {
    const confirm = (start?: string, end?: string) => {
      updatePricingModelEffectiveDates(id, start, end);
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(false);
    setActivateDialogTitle(
      t('serviceLevelModels.confirmActivateServiceLevelModelTitle'),
    );
    setActivateDialogMessage(
      t('serviceLevelModels.confirmActivateServiceLevelModelMessage'),
    );
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const deactivatePricingModel = (id: string) => {
    const confirm = (start?: string, end?: string) => {
      updatePricingModelEffectiveDates(id, start, end);
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(true);
    setActivateDialogTitle(
      t('serviceLevelModels.confirmDeactivateServiceLevelModelTitle'),
    );
    setActivateDialogMessage(
      t('serviceLevelModels.confirmDeactivateServiceLevelModelMessage'),
    );
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedServiceLevelModel) {
      dispatch(
        accountingSlice.deleteTransportationServiceAsync(
          selectedServiceLevelModel.transportationServiceUUID!,
        ),
      );
    }
    setConfirmDialogOpen(false);
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('serviceLevelModels.name'),
      sortable: false,
      minWidth: 400,
    },
    {
      field: 'state',
      headerName: t('serviceLevelModels.status'),
      sortable: false,
      minWidth: 87,
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%">
          <CommonEntityStateChip state={params.row.state} />
        </Box>
      ),
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      flex: 1,

      renderCell: (params) => {
        if (
          !isSuperReaderUser &&
          params.row.transportationServiceUUID === hoveredRow
        ) {
          return (
            <Box className={classes.moreButtonContainer}>
              <IconButton
                className={classes.moreButton}
                onClick={(event) =>
                  handleMenuClick(event, params.row.transportationServiceUUID)
                }>
                <MoreVert />
              </IconButton>
            </Box>
          );
        }
      },
    },
  ];

  return (
    <StyledPaper>
      <Box className={classes.container}>
        <PageHeader
          title={t('serviceLevelModels.title')}
          returnLink="/settings"
          returnLinkText={t('settings.title')}
        />
        <Stack direction="column" className={classes.contentContainer}>
          <Stack direction="row" className={classes.subHeader}>
            <Stack direction="row">
              <TextSearch
                id="text-search"
                className={classes.search}
                icon={<SearchIcon />}
                label={t('common.search')}
                currentSearchFilter={searchText}
                onSearchChange={handleSearchTextChange}
              />
              <TextField
                select
                className={classes.statusSelect}
                label={t('serviceLevelModels.status')}
                variant="outlined"
                value={selectedStatus}
                onChange={(evt) => handleStatusChange(evt.target.value)}>
                {statuses?.map((status) => {
                  return (
                    <MenuItem value={status.key} key={status.key}>
                      {status.value}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Stack>
            {!isSuperReaderUser && (
              <Button
                className={classes.addButton}
                color="primary"
                variant="contained"
                onClick={handleAddServiceLevelModelClick}>
                {t('common.add')}
              </Button>
            )}
          </Stack>
          <StyledDataGridPro
            disableColumnMenu={true}
            getRowId={(row) => row.transportationServiceUUID}
            rows={filteredServiceLeveModels ?? []}
            columns={columns}
            rowCount={filteredServiceLeveModels?.length}
            pagination={true}
            autoHeight
            onRowClick={handleRowClick}
            getRowClassName={(params) =>
              params.row.transportationServiceUUID === menuOpenedRow
                ? classes.hoveredRow
                : ''
            }
            slotProps={{
              row: {
                onMouseEnter: (event) => {
                  const id = event.currentTarget.dataset.id;
                  setHoveredRow(id);
                },
              },
            }}
          />
        </Stack>
      </Box>
      <StyledMenu
        anchorEl={menuAnchorEl}
        open={!!menuAnchorEl}
        onClose={handleMenuClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 116,
        }}>
        {isEditable(selectedServiceLevelModel?.state as CommonEntityState) && (
          <StyledMenuItem onClick={() => handleEditClick()}>
            {t('serviceLevelModels.edit')}
          </StyledMenuItem>
        )}
        {(selectedServiceLevelModel?.state === CommonEntityState.Draft ||
          selectedServiceLevelModel?.state === CommonEntityState.Inactive) && (
          <StyledMenuItem onClick={() => handleSetActiveClick()}>
            {t('serviceLevelModels.setActive')}
          </StyledMenuItem>
        )}
        {selectedServiceLevelModel?.state === CommonEntityState.Active && (
          <StyledMenuItem onClick={() => handleSetInactiveClick()}>
            {t('serviceLevelModels.setInactive')}
          </StyledMenuItem>
        )}
        <StyledMenuItem onClick={() => handleDuplicateClick()}>
          {t('serviceLevelModels.duplicate')}
        </StyledMenuItem>
        {(selectedServiceLevelModel?.state === CommonEntityState.Draft ||
          selectedServiceLevelModel?.state === CommonEntityState.Inactive) && (
          <StyledMenuItem onClick={() => handleDeleteClick()}>
            {t('serviceLevelModels.delete')}
          </StyledMenuItem>
        )}
      </StyledMenu>
      <ServiceLevelModelDialog
        open={serviceLevelModelDialogOpen}
        action={serviceLevelModelDialogAction}
        close={handleServiceLevelModelDialogClose}
        serviceLevelModel={selectedServiceLevelModel ?? undefined}
      />
      <ActivateDialog
        title={activateDialogTitle}
        message={activateDialogMessage}
        open={activateDialogOpen}
        forActivate={!isDeactivate}
        handleOk={activateDialogPostiveFn}
        handleCancel={activateDialogNegativeFn}
      />
      <ConfirmationDialog
        title={t('serviceLevelModels.confirmDeleteTitle')}
        message={t('serviceLevelModels.confirmDeleteMessage')}
        open={confirmDialogOpen}
        positiveAction={confirmDelete}
        negativeAction={cancelDelete}
      />
    </StyledPaper>
  );
}
